import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TriageQueue = () => {
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [sortBy, setSortBy] = useState('priority');

  const triageItems = [
    {
      id: 'triage_001',
      title: 'Viral video claiming EU vaccine mandate for children',
      description: 'TikTok video with 2.3M views spreading false information about mandatory vaccination policies',
      priority: 'critical',
      harmScore: 0.94,
      viralityScore: 0.87,
      platform: 'TikTok',
      language: 'German',
      region: 'Central Europe',
      submittedBy: 'EDMO Hub Germany',
      timeSubmitted: new Date(Date.now() - 1800000),
      estimatedReach: 2300000,
      engagementRate: 0.12,
      tags: ['health', 'policy', 'children'],
      status: 'pending'
    },
    {
      id: 'triage_002',
      title: 'False claims about EU energy crisis response',
      description: 'Coordinated posts across multiple platforms spreading misinformation about energy policies',
      priority: 'high',
      harmScore: 0.81,
      viralityScore: 0.73,
      platform: 'Facebook',
      language: 'French',
      region: 'Western Europe',
      submittedBy: 'Platform Signal',
      timeSubmitted: new Date(Date.now() - 3600000),
      estimatedReach: 1800000,
      engagementRate: 0.08,
      tags: ['energy', 'policy', 'economy'],
      status: 'assigned'
    },
    {
      id: 'triage_003',
      title: 'Deepfake video of EU official making controversial statement',
      description: 'AI-generated video showing fabricated speech about immigration policies',
      priority: 'critical',
      harmScore: 0.96,
      viralityScore: 0.65,
      platform: 'Twitter',
      language: 'English',
      region: 'Multi-regional',
      submittedBy: 'OSINT Source',
      timeSubmitted: new Date(Date.now() - 2700000),
      estimatedReach: 950000,
      engagementRate: 0.15,
      tags: ['deepfake', 'politics', 'immigration'],
      status: 'in-review'
    },
    {
      id: 'triage_004',
      title: 'Misleading infographic about EU climate targets',
      description: 'Widely shared graphic with manipulated statistics about carbon emission goals',
      priority: 'medium',
      harmScore: 0.67,
      viralityScore: 0.58,
      platform: 'Instagram',
      language: 'Spanish',
      region: 'Southern Europe',
      submittedBy: 'EFCSN Member',
      timeSubmitted: new Date(Date.now() - 5400000),
      estimatedReach: 720000,
      engagementRate: 0.06,
      tags: ['climate', 'statistics', 'environment'],
      status: 'pending'
    },
    {
      id: 'triage_005',
      title: 'Conspiracy theory about EU digital identity system',
      description: 'False claims linking eIDAS2 to surveillance and privacy violations',
      priority: 'high',
      harmScore: 0.78,
      viralityScore: 0.82,
      platform: 'Telegram',
      language: 'Italian',
      region: 'Southern Europe',
      submittedBy: 'Platform Signal',
      timeSubmitted: new Date(Date.now() - 7200000),
      estimatedReach: 1200000,
      engagementRate: 0.09,
      tags: ['privacy', 'technology', 'conspiracy'],
      status: 'pending'
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-blue-600 bg-blue-50';
      case 'assigned': return 'text-purple-600 bg-purple-50';
      case 'in-review': return 'text-orange-600 bg-orange-50';
      case 'completed': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const filteredItems = triageItems?.filter(item => 
    selectedPriority === 'all' || item?.priority === selectedPriority
  );

  const sortedItems = [...filteredItems]?.sort((a, b) => {
    switch (sortBy) {
      case 'priority':
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        return priorityOrder?.[b?.priority] - priorityOrder?.[a?.priority];
      case 'harm':
        return b?.harmScore - a?.harmScore;
      case 'virality':
        return b?.viralityScore - a?.viralityScore;
      case 'time':
        return b?.timeSubmitted - a?.timeSubmitted;
      default:
        return 0;
    }
  });

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Triage Queue</h3>
              <p className="text-sm text-muted-foreground">AI-prioritized claims requiring immediate attention</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="Filter">
              Filter
            </Button>
            <Button variant="outline" size="sm" iconName="Download">
              Export
            </Button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e?.target?.value)}
            className="px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="priority">Sort by Priority</option>
            <option value="harm">Sort by Harm Score</option>
            <option value="virality">Sort by Virality</option>
            <option value="time">Sort by Time</option>
          </select>

          <div className="flex items-center space-x-2 px-3 py-2 bg-primary/10 rounded-md">
            <Icon name="Clock" size={14} className="text-primary" />
            <span className="text-xs font-medium text-primary">Auto-refresh: 30s</span>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {sortedItems?.map((item) => (
            <div
              key={item?.id}
              className="p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(item?.priority)}`}>
                      {item?.priority?.toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item?.status)}`}>
                      {item?.status?.replace('-', ' ')?.toUpperCase()}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatTimeAgo(item?.timeSubmitted)}</span>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">{item?.title}</h4>
                  <p className="text-sm text-muted-foreground mb-3">{item?.description}</p>
                </div>
                <Button variant="ghost" size="icon" iconName="MoreHorizontal" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div className="flex items-center space-x-2">
                  <Icon name="TrendingUp" size={14} className="text-red-500" />
                  <div>
                    <div className="text-xs text-muted-foreground">Harm Score</div>
                    <div className="text-sm font-medium text-foreground">{(item?.harmScore * 100)?.toFixed(0)}%</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Zap" size={14} className="text-orange-500" />
                  <div>
                    <div className="text-xs text-muted-foreground">Virality</div>
                    <div className="text-sm font-medium text-foreground">{(item?.viralityScore * 100)?.toFixed(0)}%</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={14} className="text-blue-500" />
                  <div>
                    <div className="text-xs text-muted-foreground">Reach</div>
                    <div className="text-sm font-medium text-foreground">{(item?.estimatedReach / 1000000)?.toFixed(1)}M</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Globe" size={14} className="text-green-500" />
                  <div>
                    <div className="text-xs text-muted-foreground">Platform</div>
                    <div className="text-sm font-medium text-foreground">{item?.platform}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-xs text-muted-foreground">
                    <Icon name="MapPin" size={12} className="inline mr-1" />
                    {item?.region}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <Icon name="Globe" size={12} className="inline mr-1" />
                    {item?.language}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <Icon name="User" size={12} className="inline mr-1" />
                    {item?.submittedBy}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    Assign
                  </Button>
                  <Button variant="default" size="sm">
                    Review
                  </Button>
                </div>
              </div>

              {item?.tags?.length > 0 && (
                <div className="flex items-center space-x-2 mt-3">
                  {item?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-secondary/10 text-secondary rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TriageQueue;